import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { IconDocument, IconShield, IconUserCog } from '../../shared/icons';

interface AuthLayoutProps {
  kicker: string;
  heading: string;
  children: ReactNode;
}

/**
 * The signed-out chrome: the red brand slab beside a column of content.
 * Sign-in, "forgot password" and the set-password screens all sit in it, so a
 * user following a link from their inbox lands somewhere that looks like the
 * place they were invited to.
 */
export function AuthLayout({ kicker, heading, children }: AuthLayoutProps) {
  const sideRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // The hero should be as large as the panel allows, but the form and hero
  // move as one block. Work out how much height the hero may take once the
  // form is accounted for, and hand that to CSS as a custom property. The
  // hero's width is then capped so its height (width / 3, the SVG's ratio)
  // never exceeds that room.
  useLayoutEffect(() => {
    const side = sideRef.current;
    const hero = heroRef.current;
    const form = formRef.current;
    if (!side || !hero || !form) return;

    const update = () => {
      const style = getComputedStyle(side);
      const padding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      const heroMargin = parseFloat(getComputedStyle(hero).marginBottom) || 0;
      const room = side.clientHeight - padding - heroMargin - form.offsetHeight;
      const value = `${Math.max(0, Math.floor(room))}px`;
      if (side.style.getPropertyValue('--auth-hero-room') !== value) {
        side.style.setProperty('--auth-hero-room', value);
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(side);
    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <div className="auth-brand-mark">
          <div className="auth-brand-logo">
            <img src="/brand/ejot-logo.png" alt="EJOT" />
          </div>
          <span className="auth-brand-kicker">Engineering Platform</span>
        </div>

        <div className="auth-brand-body">
          <h1 className="auth-brand-heading">
            Fastening design tools,
            <br />
            in one workspace.
          </h1>
          <p className="auth-brand-lede">
            Sign in to reach EJOT's fastening design tools. Every account gets the full set, and
            your projects, reports and revisions stay yours.
          </p>
          <div className="auth-brand-points">
            <div className="auth-brand-point">
              <IconShield size={16} />
              <span>ETA-backed design methods</span>
            </div>
            <div className="auth-brand-point">
              <IconDocument size={16} />
              <span>Signed PDF calculation reports</span>
            </div>
            <div className="auth-brand-point">
              <IconUserCog size={16} />
              <span>Every tool, on every account</span>
            </div>
          </div>
        </div>

        <div className="auth-brand-legal">© {new Date().getFullYear()} EJOT</div>
      </div>

      <div className="auth-form-side" ref={sideRef}>
        <div className="auth-hero" ref={heroRef}>
          <img
            src="/brand/ejot-engineering-platform-hero.svg"
            alt="EJOT Engineering Platform"
          />
        </div>
        <div className="auth-form-column" ref={formRef}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="section-title">{kicker}</span>
            <h2 className="auth-heading">{heading}</h2>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
